import { useState } from 'react'
import type { ReactNode } from 'react'
import { Plus, ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  AUDIENCE_CARD_GRID_CLASS,
  useAudienceGridColumns,
} from '../hooks/useAudienceGridColumns'

interface CollapsibleAudienceGridProps {
  cards: Array<ReactNode>
  /** Merged with the default audience grid layout classes */
  className?: string
}

export function CollapsibleAudienceGrid({ cards, className }: CollapsibleAudienceGridProps) {
  const { t } = useTranslation('app')
  const columnsPerRow = useAudienceGridColumns()
  const [expanded, setExpanded] = useState(false)

  if (cards.length === 0) {
    return null
  }

  const visibleCards = expanded ? cards : cards.slice(0, columnsPerRow)
  const canToggle = cards.length > columnsPerRow

  return (
    <div className="flex flex-col gap-4">
      <div className={cn(AUDIENCE_CARD_GRID_CLASS, className)}>{visibleCards}</div>
      {canToggle && (
        <div className="flex items-center justify-center gap-4">
          {expanded ? (
            <button
              type="button"
              data-testid="audiences-load-less-button"
              onClick={() => setExpanded(false)}
              className="flex items-center gap-1.5 text-base font-medium text-foreground hover:text-primary transition-colors"
            >
              {t('audiences.loadLess')}
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="audiences-load-more-button"
              onClick={() => setExpanded(true)}
              className="flex items-center gap-1.5 text-base font-medium text-foreground hover:text-primary transition-colors"
            >
              {t('audiences.loadMore')}
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
